import React, { useContext, useEffect } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { useIntl, FormattedMessage } from 'react-intl'

import * as Analytics from 'analytics/analytics'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import Heading from 'LuxKit/Typography/Heading'
import PayAtPropertyPill from 'components/Common/PayAtPropertyPill'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import BedbankCancellationPolicy from '../BedbankCancellationPolicy'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import CurrencyContext from 'contexts/currencyContext'
import Sheet from 'components/utils/Sheet'
import { mediaQueryOnly } from 'components/utils/breakpoint'
import FormatCurrency from 'components/Common/FormatCurrency'
import LuxPlusPriceGuard from 'luxPlus/components/LuxPlusPriceGuard'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import { useAppSelector } from 'hooks/reduxHooks'
import { getIsLuxPlusLPPEnabled } from 'luxPlus/selectors/featureToggle'

const PaymentOptionsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  padding-top: 1rem;
  
  ${mediaQueryOnly.mobile} {
    grid-template-columns: 1fr;
  }
`

const DescriptionText = styled(BodyText)`
  min-height: ${rem(60)}; // Approximately 3 lines of text
`

const trackPayAtPropertyModalEvent = (action: string, type: 'interaction' | 'nonInteraction') => {
  Analytics.trackClientEvent({
    subject: 'choose_payment_option_modal',
    action,
    category: 'pay_at_property',
    type,
  })
}

function BedbankPayAtPropertyModal({ payAtPropertyRate, lePaymentNowRate, propertyCountryCode }: {
  payAtPropertyRate: App.BedbankRate
  lePaymentNowRate: App.BedbankRate
  propertyCountryCode: string
}) {
  const intl = useIntl()
  const { resolve } = useModalElementContext<App.BedbankRate | undefined>()
  const contextCurrency = useContext(CurrencyContext)
  const luxPlusLPPEnabled = useAppSelector(state => getIsLuxPlusLPPEnabled(state))

  useEffect(() => {
    trackPayAtPropertyModalEvent('impression', 'nonInteraction')
  }, [])

  const onSelectPayAtProperty = () => {
    trackPayAtPropertyModalEvent('select_pay_at_property', 'interaction')
    resolve(payAtPropertyRate)
  }

  const onSelectPayNow = () => {
    trackPayAtPropertyModalEvent('select_pay_now', 'interaction')
    resolve(lePaymentNowRate)
  }

  const onClose = () => {
    resolve(undefined)
  }

  const payAtPropertyTotal =
    (payAtPropertyRate.totals.inclusive ?? 0) +
    (payAtPropertyRate.totals.propertyFees ?? 0)

  const payNowTotal =
    (lePaymentNowRate.totals.inclusive ?? 0) +
    (lePaymentNowRate.totals.propertyFees ?? 0)

  const totalMemberPricesAfterTax = lePaymentNowRate.totals.memberInclusive ?? 0

  const payAtPropertyAmount = payAtPropertyRate.paymentMethod === 'propertyCollect' ? (payAtPropertyRate.payAtPropertyTotals.inclusive) + (payAtPropertyRate.payAtPropertyTotals.propertyFees ?? 0) : 0
  const payAtPropertyCurrencyCode = payAtPropertyRate.paymentMethod === 'propertyCollect' ? payAtPropertyRate.payAtPropertyTotals.currencyCode : ''

  const sameCurrency = payAtPropertyRate.paymentMethod === 'propertyCollect' ? payAtPropertyRate.payAtPropertyTotals.currencyCode === contextCurrency : false

  const hasLuxPlusPrice = (totalMemberPricesAfterTax ?? 0) > 0 && luxPlusLPPEnabled

  return (
    <ModalBase size="L">
      <ModalHeader
        title={intl.formatMessage({
          id: 'JqCh08VbBeJo9pFvF4Aq',
          defaultMessage: 'Your payment options',
          description: 'Title for bedbank pay at property modal showing payment choices',
        })}
        onCloseButtonClick={onClose}
      />
      <ModalBody>
        <ModalContent>
          <PaymentOptionsContainer>
            {/* Pay at Property Option */}
            <Sheet
              border="neutral-five"
              borderRadius="S"
              padding={20}
            >
              <VerticalSpacer gap={12}>
                <Group
                  direction="horizontal"
                  gap={8}
                  horizontalAlign="space-between"
                >
                  <Heading variant="heading4">
                    <FormattedMessage
                      id="sgn-NS8GzoPJJXuXQgsK"
                      defaultMessage="Pay $0 now"
                      description="Section heading for pay at property option on the offer detail page"
                    />
                  </Heading>
                  <PayAtPropertyPill hasMultipleOptions={false} />
                </Group>

                { !sameCurrency && <DescriptionText variant="medium" colour="neutral-two">
                  <FormattedMessage
                    id="8Xja8hHlP5h7Pk_no4vC"
                    defaultMessage="Prices in {currency} are estimated, you will pay in the property’s preferred currency ({amount})"
                    description="Explanation that pay-at-property prices are estimated in user currency and actual payment will be in property currency"
                    values={{
                      currency: contextCurrency,
                      amount: <FormatCurrency value={payAtPropertyAmount} currency={payAtPropertyCurrencyCode} countryCode={propertyCountryCode} />,
                    }}
                  />
                </DescriptionText>}

                <BedbankCancellationPolicy
                  rate={payAtPropertyRate}
                  showNonCancellation
                  showHotelOnly={false}
                />
                <VerticalSpacer gap={hasLuxPlusPrice ? 30 : 12}>
                  <Group direction="vertical" gap={2}>
                    <PriceRowPriceCaption>
                      {intl.formatMessage(
                        {
                          id: 'WJfp1mfVoBPd5B3l4RVD',
                          defaultMessage: '{count, plural, one {# night} other {# nights}}',
                          description: 'Night count for pay-at-property rate on the offer detail page',
                        },
                        { count: payAtPropertyRate.nights },
                      )}
                    </PriceRowPriceCaption>

                    <PriceRowPrice price={payAtPropertyTotal} saleUnit="total" size="L" />

                    <PriceRowTaxesAndFees />
                  </Group>

                  <TextButton
                    size="large"
                    kind="primary"
                    fit="full-width"
                    onClick={onSelectPayAtProperty}
                  >
                    <FormattedMessage
                      id="4-AkdhSguWIycROZnzFF"
                      defaultMessage="Pay at property"
                      description="Button to select pay-at-property payment option on the form"
                    />
                  </TextButton>
                </VerticalSpacer>
              </VerticalSpacer>
            </Sheet>

            {/* Pay Now Option */}
            <Sheet
              border="neutral-five"
              borderRadius="S"
              padding={20}
            >
              <VerticalSpacer gap={12}>
                <Heading variant="heading4">
                  <FormattedMessage
                    id="vbhB-HGmOpGRx1zXfkZe"
                    defaultMessage="Pay in full now"
                    description="Section heading for pay in full option on the offer detail page"
                  />
                </Heading>

                { !sameCurrency && <DescriptionText variant="medium" colour="neutral-two">
                  <FormattedMessage
                    id="0O2fsvxkOVLdmfQgeKBC"
                    defaultMessage="We process your payment now in your local currency"
                    description="Explanation that pay-now option processes payment in user’s local currency"
                  />
                </DescriptionText>}

                <BedbankCancellationPolicy
                  rate={lePaymentNowRate}
                  showNonCancellation
                  showHotelOnly={false}
                />
                <Group direction="vertical" gap={2}>
                  <PriceRowPriceCaption>
                    {intl.formatMessage(
                      {
                        id: 'jUE4uud3J8jYI8615-hZ',
                        defaultMessage: '{count, plural, one {# night} other {# nights}}',
                        description: 'Pluralised count of nights for the pay-now rate option in the hotel booking pricing',
                      },
                      { count: lePaymentNowRate.nights },
                    )}
                  </PriceRowPriceCaption>
                  <LuxPlusPriceGuard
                    showMemberPrice={hasLuxPlusPrice}
                    fallbackPrice={<PriceRowPrice
                      size="M"
                      signDisplay="negative"
                      price={payNowTotal}
                      saleUnit="total"
                    />}
                  >
                    <LuxPlusPriceStack
                      size="M"
                      signDisplay="negative"
                      price={payNowTotal}
                      memberPrice={totalMemberPricesAfterTax}
                      saleUnit="total"
                    />
                  </LuxPlusPriceGuard>

                  <PriceRowTaxesAndFees />
                </Group>

                <TextButton
                  size="large"
                  kind="primary"
                  fit="full-width"
                  onClick={onSelectPayNow}
                >
                  <FormattedMessage
                    id="tJnR_dIaXR4yONsUZeJF"
                    defaultMessage="Pay now"
                    description="Button to select pay-now payment option during the checkout and payment process"
                  />
                </TextButton>
              </VerticalSpacer>
            </Sheet>
          </PaymentOptionsContainer>
        </ModalContent>
      </ModalBody>
    </ModalBase>
  )
}

export default BedbankPayAtPropertyModal
